import { ChangeDetectorRef, Component, ViewChildren } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Params } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HotToastService } from "@ngneat/hot-toast";
import { catchError, defer, Observable, of, tap, throwError } from "rxjs";
import { CardWrapperComponent } from "../../../form/components/card-wrapper/card-wrapper.component";
import { FileUploadComponent } from "../../../form/components/file-upload/file-upload.component";
import { FormModel } from "../../../form/form-models/form-model";
import { BasePage } from "./base-page";

@Component({
  template: "",
})
export class EditPage extends BasePage {
  isSaving: boolean = false;
  htmlFormId: string = "form";
  errors: string[] = [];
  params: Params = {};
  queryParams: Params = {};
  isNew: boolean = false;

  focusOnLoad: string = "";

  @ViewChildren(CardWrapperComponent) cardWrappers!: CardWrapperComponent[];
  @ViewChildren(FileUploadComponent) fileUploads!: FileUploadComponent[];

  constructor(
    protected override route?: ActivatedRoute,
    protected override modal?: NgbModal,
    protected override toast?: HotToastService,
    protected cdr?: ChangeDetectorRef
  ) {
    super(route, modal, toast);
    this.route?.fragment.subscribe((fragment) => {
      this.fragment = fragment;
    });
  }

  init() {
    console.log("INIT EDITPAGE");
    this.route?.params.subscribe((params) => {
      this.route?.queryParams.subscribe((queryParams) => {
        console.log("PARAMS", params);
        console.log("QUERYPARAMS", queryParams);
        this.params = params;
        this.queryParams = queryParams;
        if (params["id"] && params["id"] > 0) {
          this.getItem(params["id"]);
        } else {
          this.isNew = true;
          this.getNewItem();
        }
      });
    });
    this.scrollToTop();
  }

  getNewItem() {
    this.isLoaded();
  }

  getItem(id: number) {
    this.isLoaded();
  }

  saveFunction(model: FormModel<any>, onSuccess?: (x: any) => void, onError?: (x: any) => void) {
    this.saveFunctionObs(model)
      .pipe(
        catchError((error) => {
          if (onError) {
            return of(false);
          }
          return throwError(error);
        })
      )
      .subscribe((result) => {
        if (result) {
          this.saved();
          if (onSuccess) {
            onSuccess(this);
          }
        } else {
          this.notSaved();
          if (onError) {
            onError(this);
          }
        }
      });
  }

  saveItem(
    form: FormModel<any> | null = null,
    onSuccess?: (x: any) => void,
    onError?: (x: any) => void
  ) {
    if (form)
      if (this.validateForm(form)) {
        this.isSaving = true;
        this.saveFunction(form, onSuccess, onError);
      } else {
        form.markAllAsTouched();
        this.updateValueAndValidity(form);
        this.generateErrorMessages(form);
      }
  }

  saveFunctionObs(model: FormModel<any>): Observable<boolean> {
    return of(true);
  }

  saveItemObs(form: FormModel<any> | null = null): Observable<boolean> {
    return defer(() => {
      if (!form) return of(false);
      if (!this.validateForm(form)) {
        form.markAllAsTouched();
        this.generateErrorMessages(form);
        return of(false);
      }
      this.isSaving = true;
      return this.saveFunctionObs(form).pipe(
        tap(
          () => (this.isSaving = false),
          catchError((error) => {
            this.isSaving = false;
            return of(false);
          })
        )
      );
    });
  }

  /**
   * load dropdowns,
   * set valuechanges,
   * set rightnav
   */
  isLoaded() {
    this.isLoading = false;

    this.loadDropdowns();
    this.setValueChanges();
    this.setRightNav();
    if (this.focusOnLoad) this.focus(this.focusOnLoad);
  }

  setValueChanges() {}

  loadDropdowns() {}

  validateControl(control: FormControl) {
    if (control.valid) {
      return true;
    } else {
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
      return false;
    }
  }

  validateForm(form: FormGroup | FormGroup[]) {
    console.log(form);
    if (Array.isArray(form)) {
      let isValid = true;

      form.forEach((group: FormGroup) => {
        if (group instanceof FormGroup) {
          if (!group.valid) {
            group.markAllAsTouched();
            this.scrollToFirstInvalidControl();
            this.generateErrorMessages(group);
            isValid = false;
          }
        }
      });

      return isValid;
    } else {
      if (form instanceof FormGroup) {
        if (form.valid) {
          return true;
        } else {
          form.markAllAsTouched();
          this.scrollToFirstInvalidControl();
          this.generateErrorMessages(form);
          return false;
        }
      } else {
        throw new Error("Invalid argument: form must be a FormGroup or an array of FormGroup");
      }
    }
  }

  markAsDirty(control: FormGroup | AbstractControl) {
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();

    if (control instanceof FormGroup) {
      for (const key of Object.keys(control.controls)) {
        this.markAsDirty(control.controls[key]);
      }
    }

    if (control instanceof FormArray) {
      for (const key of Object.keys(control.controls)) {
        const index = key as any;
        this.markAsDirty(control.controls[index]);
      }
    }
    return;
  }

  updateValueAndValidity(control: FormGroup | AbstractControl) {
    if (control instanceof FormControl) {
      if (control.invalid) {
        console.log("update control");
        control.updateValueAndValidity();
      }
      return;
    }
    if (control instanceof FormGroup) {
      for (const key of Object.keys(control.controls)) {
        this.updateValueAndValidity(control.controls[key]);
      }
    }

    if (control instanceof FormArray) {
      for (const key of Object.keys(control.controls)) {
        console.log(key);
        const index = key as any;
        this.updateValueAndValidity(control.controls[index]);
      }
    }
  }

  saved() {
    this.isSaving = false;
    /*if (this.toast) {
      this.toast.success("Wijzigingen succesvol bewaard");
    }*/
  }

  notSaved() {
    this.isSaving = false;
    if (this.toast) {
      this.toast.error("Er liep iets fout bij het bewaren van de verrichting");
    }
  }

  //returns if there is a first invalid control to focus on
  scrollToFirstInvalidControl(): boolean {
    const firstInvalidControl = document.querySelector(
      "div.invalid-feedback, input.ng-invalid, select.ng-invalid, textarea.ng-invalid, ng-select.ng-invalid, .p-element.ng-invalid"
    ) as HTMLElement;
    if (firstInvalidControl) {
      firstInvalidControl.focus();
      window.scroll({
        top: this.getTopOffset(firstInvalidControl),
        left: 0,
        behavior: "smooth",
      });
      return true;
    }
    return false;
  }

  generateErrorMessages(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((controlName) => {
      let control = formGroup.controls[controlName];
      let errors: any = control.errors;
      if (errors === null || errors.count === 0) {
        return;
      }
      console.log("ERROR:" + controlName);
      console.log(errors);
      // Handle the 'required' case
      if (errors.required) {
        this.errors.push(`${controlName} is required`);
      }

      // Handle the 'requiredTrue' case
      if (errors.requiredTrue) {
        this.errors.push(`${controlName} is required`);
      }

      // Handle 'minlength' case
      if (errors.minlength) {
        this.errors.push(`${controlName} minimum length is ${errors.minlength.requiredLength}.`);
      }

      if (errors.invalidEmail) {
        this.errors.push(`${controlName} is not a valid email address`);
      }
    });
  }
}
